<template>
	<div class="column-selector" role="dialog" aria-modal="true">
		<div class="header">
			<div class="header-top">
				<span v-if="title" class="title">{{ title }}</span>
				<button
					type="button"
					class="icon-button"
					:aria-label="t('filters.close')"
					:title="t('filters.close')"
					@click="$emit('close')"
				>
					<AppIcon :name="IconName.Close" class="close-icon" />
				</button>
			</div>
			<p v-if="note" class="note">{{ note }}</p>
		</div>

		<p v-if="sectionLabel && !hasGroups" class="section-label">{{ sectionLabel }}</p>

		<ul class="column-list">
			<li v-if="pinnedColumn" class="column-item pinned">
				<span class="drag-handle placeholder" aria-hidden="true" />
				<ArkCheckbox :checked="true" :disabled="true" />
				<span class="col-label">{{ pinnedColumn.label }}</span>
			</li>
			<template v-for="group in groupedColumns" :key="group.key">
				<li
					v-if="hasGroups"
					class="column-group-header"
					:class="{
						dragging: dragGroupKey === group.key,
						'drag-over': dragOverGroupKey === group.key,
						'not-draggable': !groupsReorderable,
					}"
					:draggable="groupsReorderable"
					@dragstart="groupsReorderable ? onGroupDragStart(group.key) : undefined"
					@dragover.prevent="onGroupHeaderDragOver(group.key)"
					@drop.prevent="onGroupHeaderDrop(group.key)"
					@dragend="onGroupDragEnd"
				>
					<span class="drag-handle" :class="{ placeholder: !groupsReorderable }" aria-hidden="true">⠿</span>
					<ArkCheckbox
						:checked="isGroupFullyVisible(group.key)"
						:disabled="isGroupFullyLocked(group.key)"
						@change="toggleGroup(group.key)"
					/>
					<span class="group-label">{{ group.label }}</span>
				</li>
				<li
					v-for="col in group.columns"
					:key="col.key"
					class="column-item"
					:class="{
						'drag-over': dragOverIndex === indexOf(col.key),
						dragging: dragSrcIndex === indexOf(col.key),
						grouped: hasGroups,
						locked: col.locked,
					}"
					:draggable="!col.locked && !col.fixedGroup"
					@dragstart="col.locked || col.fixedGroup ? undefined : onDragStart(indexOf(col.key))"
					@dragover.prevent="col.locked ? undefined : onDragOver(indexOf(col.key))"
					@drop="col.locked ? undefined : onDrop(indexOf(col.key))"
					@dragend="onDragEnd"
				>
					<span class="drag-handle" :class="{ placeholder: col.locked || col.fixedGroup }" aria-hidden="true">⠿</span>
					<ArkCheckbox
						:checked="col.visible"
						:disabled="col.locked"
						@change="col.locked ? undefined : (col.visible = !col.visible)"
					/>
					<span class="col-label" @click="col.locked ? undefined : (col.visible = !col.visible)">{{ col.label }}</span>
				</li>
			</template>
		</ul>

		<div class="actions">
			<Button variant="tertiary" size="s" @click="reset">{{ t("column-selector.reset") }}</Button>
			<Button variant="primary" size="s" @click="save">{{ t("column-selector.save") }}</Button>
		</div>
	</div>
</template>

<script lang="ts" setup>
import { ref, watch, computed } from "vue"
import { useI18n } from "vue-i18n"
import AppIcon from "@/components/AppIcon.vue"
import { IconName } from "@/components/icons/registry"
import Button from "@/components/ui/Button.vue"
import ArkCheckbox from "@/components/ui/ArkCheckbox.vue"

const { t } = useI18n()

interface SelectorColumn {
	key: string
	label: string
	visible: boolean
	group?: string
	locked?: boolean
	// can't be dragged into another group, but visibility stays toggleable (unlike locked)
	fixedGroup?: boolean
}

const props = withDefaults(
	defineProps<{
		columns: SelectorColumn[]
		defaultColumns: SelectorColumn[]
		pinnedKey?: string
		title?: string
		sectionLabel?: string
		note?: string
		groupLabels?: Record<string, string>
		lockedGroups?: string[]
		// whole groups can be dragged to reorder them relative to each other (the original, still-default behavior)
		groupsReorderable?: boolean
		// individual columns can be dragged into a different group instead of only reordering within their own
		crossGroupMove?: boolean
	}>(),
	{
		groupsReorderable: true,
		crossGroupMove: false,
	}
)

const emit = defineEmits<{
	(e: "save", columns: SelectorColumn[]): void
	(e: "close"): void
}>()

const pinnedColumn = computed(() => (props.pinnedKey ? props.columns.find(c => c.key === props.pinnedKey) : undefined))

function toDraggable(cols: SelectorColumn[]): SelectorColumn[] {
	return cols.filter(c => c.key !== props.pinnedKey).map(c => ({ ...c }))
}

const draggableColumns = ref<SelectorColumn[]>(toDraggable(props.columns))

watch(
	() => props.columns,
	cols => {
		draggableColumns.value = toDraggable(cols)
	}
)

const hasGroups = computed(() => draggableColumns.value.some(c => c.group != null))

const groupedColumns = computed(() => {
	const groups: { key: string; label: string; columns: SelectorColumn[] }[] = []
	for (const col of draggableColumns.value) {
		const key = col.group ?? ""
		let group = groups.find(g => g.key === key)
		if (!group) {
			group = { key, label: props.groupLabels?.[key] ?? key, columns: [] }
			groups.push(group)
		}
		group.columns.push(col)
	}
	return groups
})

function indexOf(key: string): number {
	return draggableColumns.value.findIndex(c => c.key === key)
}

function isGroupFullyVisible(group: string): boolean {
	return draggableColumns.value.filter(c => c.group === group).every(c => c.visible)
}

function isGroupFullyLocked(group: string): boolean {
	if (props.lockedGroups?.includes(group)) return true
	return draggableColumns.value.filter(c => c.group === group).every(c => c.locked)
}

function toggleGroup(group: string): void {
	if (isGroupFullyLocked(group)) return
	const makeVisible = !isGroupFullyVisible(group)
	draggableColumns.value.forEach(c => {
		if (c.group === group && !c.locked) c.visible = makeVisible
	})
}

const dragSrcIndex = ref(-1)
const dragOverIndex = ref<number | null>(null)

function onDragStart(index: number): void {
	dragSrcIndex.value = index
}

function onDragOver(index: number): void {
	if (dragSrcIndex.value === index) return
	if (
		!props.crossGroupMove &&
		draggableColumns.value[dragSrcIndex.value]?.group !== draggableColumns.value[index]?.group
	)
		return
	dragOverIndex.value = index
}

// dropping onto an item in a different group moves the dragged column into that group (only when crossGroupMove is on)
function onDrop(targetIndex: number): void {
	if (dragSrcIndex.value === targetIndex) return
	const cols = [...draggableColumns.value]
	const targetGroup = cols[targetIndex]?.group
	if (!props.crossGroupMove && cols[dragSrcIndex.value]?.group !== targetGroup) return
	const [moved] = cols.splice(dragSrcIndex.value, 1)
	if (!moved) return
	if (props.crossGroupMove) {
		// removing the source shifted later indices down by one, so account for that when inserting
		const insertAt = dragSrcIndex.value < targetIndex ? targetIndex - 1 : targetIndex
		if (targetGroup !== undefined) moved.group = targetGroup
		cols.splice(Math.max(0, insertAt), 0, moved)
	} else {
		cols.splice(targetIndex, 0, moved)
	}
	draggableColumns.value = cols
	dragSrcIndex.value = -1
	dragOverIndex.value = null
}

function onDragEnd(): void {
	dragSrcIndex.value = -1
	dragOverIndex.value = null
}

let dragGroupKey: string | null = null
const dragOverGroupKey = ref<string | null>(null)

function onGroupDragStart(key: string): void {
	dragGroupKey = key
}

function onGroupReorderDrop(targetKey: string): void {
	if (dragGroupKey === null || dragGroupKey === targetKey) return
	const groupsByKey = new Map(groupedColumns.value.map(g => [g.key, g.columns]))
	const order = groupedColumns.value.map(g => g.key)
	const [moved] = order.splice(order.indexOf(dragGroupKey), 1)
	order.splice(order.indexOf(targetKey), 0, moved)
	draggableColumns.value = order.flatMap(key => groupsByKey.get(key) ?? [])
}

function onGroupDragEnd(): void {
	dragGroupKey = null
	dragOverGroupKey.value = null
}

// dropping an item on a group header moves it there (crossGroupMove only); dropping a header here reorders groups
function onGroupHeaderDragOver(group: string): void {
	if (dragGroupKey !== null) {
		if (dragGroupKey !== group) dragOverGroupKey.value = group
		return
	}
	if (props.crossGroupMove && dragSrcIndex.value >= 0) dragOverIndex.value = null
}

function onGroupHeaderDrop(group: string): void {
	if (dragGroupKey !== null) {
		onGroupReorderDrop(group)
		dragGroupKey = null
		dragOverGroupKey.value = null
		return
	}
	if (!props.crossGroupMove) return
	const cols = [...draggableColumns.value]
	const moved = cols[dragSrcIndex.value]
	if (!moved) return
	cols.splice(dragSrcIndex.value, 1)
	moved.group = group
	const firstOfGroup = cols.findIndex(c => c.group === group)
	cols.splice(firstOfGroup === -1 ? cols.length : firstOfGroup, 0, moved)
	draggableColumns.value = cols
	dragSrcIndex.value = -1
	dragOverIndex.value = null
}

function reset(): void {
	draggableColumns.value = toDraggable(props.defaultColumns)
}

function save(): void {
	const pinned = pinnedColumn.value ? [{ ...pinnedColumn.value, visible: true }] : []
	emit("save", [...pinned, ...draggableColumns.value])
}
</script>

<style scoped>
.column-selector {
	position: absolute;
	top: calc(100% + var(--spacing-xs));
	left: 0;
	z-index: 10;
	background-color: var(--neutral-0);
	border: var(--border-width-thin) solid var(--neutral-200);
	border-radius: var(--border-radius-md);
	box-shadow: var(--shadow-lg);
	padding: var(--spacing-md);
	min-width: 260px;
	display: flex;
	flex-direction: column;
	gap: var(--spacing-sm);
	font-family: var(--font-family-primary);

	.header {
		display: flex;
		flex-direction: column;
		gap: var(--spacing-xxs);
		padding-bottom: var(--spacing-sm);
		border-bottom: var(--border-width-thin) solid var(--neutral-200);

		.header-top {
			display: flex;
			align-items: center;
			justify-content: space-between;
			gap: var(--spacing-sm);
		}

		.title {
			font-size: var(--font-size-small);
			font-weight: var(--font-weight-semibold);
			text-transform: uppercase;
			letter-spacing: 0.02em;
			color: var(--neutral-900);
		}

		.icon-button {
			width: var(--size-icon-xl);
			height: var(--size-icon-xl);
			flex-shrink: 0;
			border: none;
			background: transparent;
			color: var(--neutral-600);
			cursor: pointer;
			padding: var(--spacing-xxs);
			display: inline-flex;
			align-items: center;
			justify-content: center;

			&:hover {
				color: var(--neutral-900);
			}

			.close-icon {
				width: var(--size-icon-sm);
				height: var(--size-icon-sm);
				stroke: currentColor;
				fill: none;
				stroke-width: 2;
				stroke-linecap: round;
			}
		}

		.note {
			font-size: var(--font-size-xs);
			color: var(--neutral-600);
			margin: 0;
		}
	}

	.section-label {
		font-size: var(--font-size-xxs);
		font-weight: var(--font-weight-medium);
		color: var(--neutral-600);
		text-transform: uppercase;
		letter-spacing: 0.05em;
		margin: 0;
	}

	.column-list {
		list-style: none;
		padding: 0;
		margin: 0;
		display: flex;
		flex-direction: column;
		gap: var(--spacing-sm);
		max-height: 50vh;
		overflow-y: auto;
	}

	.column-group-header {
		display: flex;
		align-items: center;
		gap: var(--spacing-sm);
		padding: var(--spacing-xxs) var(--space-6);
		border-radius: var(--border-radius-sm);
		cursor: grab;

		&:active {
			cursor: grabbing;
		}

		&.dragging {
			opacity: 0.4;
		}

		&.drag-over {
			outline: var(--border-width-thin) dashed var(--primary-green);
			border-radius: var(--border-radius-base);
		}

		&.not-draggable {
			cursor: default;
		}

		& + .column-group-header {
			margin-top: var(--spacing-xs);
		}

		.group-label {
			font-size: var(--font-size-xxs);
			font-weight: var(--font-weight-medium);
			color: var(--neutral-600);
			text-transform: uppercase;
			letter-spacing: 0.05em;
		}
	}

	.column-item {
		display: flex;
		align-items: center;
		gap: var(--spacing-sm);
		padding: var(--spacing-xxs) var(--space-6);
		border-radius: var(--border-radius-sm);
		cursor: grab;

		&:active {
			cursor: grabbing;
		}

		&.pinned,
		&.locked {
			cursor: default;
			opacity: 0.5;
			pointer-events: none;
		}

		&.grouped {
			padding-left: var(--spacing-lg);
		}

		&.dragging {
			background: var(--neutral-50);
			opacity: 0.4;
		}

		&.drag-over {
			border-top: var(--border-width-base) dashed var(--primary-green);
		}

		.drag-handle {
			color: var(--neutral-600);
			font-size: var(--font-size-small);
			flex-shrink: 0;
			width: 12px;

			&.placeholder {
				visibility: hidden;
			}
		}

		:deep(.ark-checkbox) {
			flex-shrink: 0;
		}

		.col-label {
			flex: 1;
			font-size: var(--font-size-small);
			font-weight: var(--font-weight-regular);
			color: var(--neutral-900);
			cursor: pointer;

			&::first-letter {
				text-transform: capitalize;
			}
		}
	}

	.actions {
		display: flex;
		gap: var(--spacing-sm);
		justify-content: center;
		padding-top: var(--spacing-sm);
		border-top: var(--border-width-thin) solid var(--neutral-200);
	}
}
</style>
